/**
 * Constants Dual-Write Test Suite
 *
 * This utility tests that constants (wallets, payment methods, roles, etc.)
 * are properly synced between Firestore and Supabase.
 *
 * Usage:
 *   import { runConstantsDualWriteTests } from './utils/constantsDualWriteTest';
 *   await runConstantsDualWriteTests(testConfig);
 */

import {
  saveWallets,
  deleteWallet,
  savePaymentMethods,
  deletePaymentMethod,
  saveRoles,
  deleteRole,
  saveProductUnits,
  deleteProductUnit,
  saveSpendingCategories,
  deleteSpendingCategory,
  saveWriteOffReasons,
  deleteWriteOffReason,
  saveReturnReasons,
  deleteReturnReason,
  saveDiscountOptions,
  deleteDiscountOption,
  savePurchaseMethods,
  deletePurchaseMethod,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, dbAtom, constantsAtom } from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

// Generate a unique ID for test items
const generateTestId = () => {
  return 'test-' + Math.random().toString(36).substring(2, 15);
};

/**
 * Test variation definitions
 */
export const CONSTANTS_TEST_VARIATIONS = [
  // Wallets
  {
    id: 'C1.1',
    name: 'Create basic wallet',
    category: 'wallets',
    type: 'create',
  },
  {
    id: 'C1.2',
    name: 'Create wallet with icon',
    category: 'wallets',
    type: 'create',
    withIcon: true,
  },
  {
    id: 'C1.3',
    name: 'Update wallet name',
    category: 'wallets',
    type: 'update',
  },
  {
    id: 'C1.4',
    name: 'Delete wallet',
    category: 'wallets',
    type: 'delete',
  },

  // Payment Methods
  {
    id: 'C2.1',
    name: 'Create cash payment method',
    category: 'payment_methods',
    type: 'create',
    isCash: true,
  },
  {
    id: 'C2.2',
    name: 'Create card payment method',
    category: 'payment_methods',
    type: 'create',
    isCash: false,
  },
  {
    id: 'C2.3',
    name: 'Create payment with commission',
    category: 'payment_methods',
    type: 'create',
    withCommission: true,
  },
  {
    id: 'C2.4',
    name: 'Update payment method',
    category: 'payment_methods',
    type: 'update',
  },
  {
    id: 'C2.5',
    name: 'Delete payment method',
    category: 'payment_methods',
    type: 'delete',
  },

  // Roles
  {
    id: 'C3.1',
    name: 'Create basic role',
    category: 'roles',
    type: 'create',
  },
  {
    id: 'C3.2',
    name: 'Create role with salary',
    category: 'roles',
    type: 'create',
    withSalary: true,
  },
  {
    id: 'C3.3',
    name: 'Create role with permissions',
    category: 'roles',
    type: 'create',
    withPermissions: true,
  },
  {
    id: 'C3.4',
    name: 'Update role',
    category: 'roles',
    type: 'update',
  },
  {
    id: 'C3.5',
    name: 'Delete role',
    category: 'roles',
    type: 'delete',
  },

  // Product Units
  {
    id: 'C4.1',
    name: 'Create product unit',
    category: 'product_units',
    type: 'create',
  },
  {
    id: 'C4.2',
    name: 'Create unit with short name',
    category: 'product_units',
    type: 'create',
    withShortName: true,
  },
  {
    id: 'C4.3',
    name: 'Delete product unit',
    category: 'product_units',
    type: 'delete',
  },

  // Spending Categories
  {
    id: 'C5.1',
    name: 'Create spending category',
    category: 'spending_categories',
    type: 'create',
  },
  {
    id: 'C5.2',
    name: 'Create admin spending category',
    category: 'spending_categories',
    type: 'create',
    isAdmin: true,
  },
  {
    id: 'C5.3',
    name: 'Delete spending category',
    category: 'spending_categories',
    type: 'delete',
  },

  // Discount Options
  {
    id: 'C6.1',
    name: 'Create discount option',
    category: 'discount_options',
    type: 'create',
  },
  {
    id: 'C6.2',
    name: 'Delete discount option',
    category: 'discount_options',
    type: 'delete',
  },

  // Write Off Reasons
  {
    id: 'C7.1',
    name: 'Create write-off reason',
    category: 'write_off_reasons',
    type: 'create',
  },
  {
    id: 'C7.2',
    name: 'Delete write-off reason',
    category: 'write_off_reasons',
    type: 'delete',
  },

  // Return Reasons
  {
    id: 'C8.1',
    name: 'Create return reason',
    category: 'return_reasons',
    type: 'create',
  },
  {
    id: 'C8.2',
    name: 'Delete return reason',
    category: 'return_reasons',
    type: 'delete',
  },

  // Purchase Methods
  {
    id: 'C9.1',
    name: 'Create purchase method',
    category: 'purchase_methods',
    type: 'create',
  },
  {
    id: 'C9.2',
    name: 'Delete purchase method',
    category: 'purchase_methods',
    type: 'delete',
  },
];

/**
 * Generate test data for a constant
 */
function generateTestData(variation, existingData) {
  const testId = generateTestId();
  const timestamp = Date.now();

  switch (variation.category) {
    case 'wallets':
      return {
        id: testId,
        text: `Test Wallet ${timestamp}`,
        name: `Test Wallet ${timestamp}`,
        icon: variation.withIcon ? 'wallet' : null,
      };

    case 'payment_methods':
      return {
        id: testId,
        name: `Test Payment ${timestamp}`,
        isCash: variation.isCash ?? false,
        comission: variation.withCommission ? 2.5 : 0,
        icon: 'credit-card',
        iconText: 'CC',
        activeInStore: {},
        destinationWalletForStore: {},
      };

    case 'roles':
      return {
        id: testId,
        name: `Test Role ${timestamp}`,
        salaryType: variation.withSalary ? 'month' : null,
        salary: variation.withSalary ? 50000 : 0,
        permissions: variation.withPermissions
          ? { canSell: true, canViewReports: true, canManageProducts: false }
          : {},
      };

    case 'product_units':
      return {
        id: testId,
        name: `Test Unit ${timestamp}`,
        text: `Test Unit ${timestamp}`,
        shortName: variation.withShortName ? 'TU' : null,
      };

    case 'spending_categories':
      return {
        id: testId,
        text: `Test Category ${timestamp}`,
        isAdmin: variation.isAdmin ?? false,
      };

    case 'discount_options':
      return {
        id: testId,
        text: `${10 + Math.floor(Math.random() * 40)}%`,
      };

    case 'write_off_reasons':
      return {
        id: testId,
        text: `Test Write-off ${timestamp}`,
      };

    case 'return_reasons':
      return {
        id: testId,
        text: `Test Return ${timestamp}`,
      };

    case 'purchase_methods':
      return {
        id: testId,
        name: `Test Purchase ${timestamp}`,
        icon: 'shopping-cart',
        iconText: 'PM',
        activeInStore: {},
      };

    default:
      return { id: testId, name: `Test ${timestamp}` };
  }
}

/**
 * Get the save function for a constant type
 */
function getSaveFunction(category) {
  const saveFunctions = {
    wallets: saveWallets,
    payment_methods: savePaymentMethods,
    roles: saveRoles,
    product_units: saveProductUnits,
    spending_categories: saveSpendingCategories,
    discount_options: saveDiscountOptions,
    write_off_reasons: saveWriteOffReasons,
    return_reasons: saveReturnReasons,
    purchase_methods: savePurchaseMethods,
  };
  return saveFunctions[category];
}

/**
 * Get the delete function for a constant type
 */
function getDeleteFunction(category) {
  const deleteFunctions = {
    wallets: deleteWallet,
    payment_methods: deletePaymentMethod,
    roles: deleteRole,
    product_units: deleteProductUnit,
    spending_categories: deleteSpendingCategory,
    discount_options: deleteDiscountOption,
    write_off_reasons: deleteWriteOffReason,
    return_reasons: deleteReturnReason,
    purchase_methods: deletePurchaseMethod,
  };
  return deleteFunctions[category];
}

/**
 * Get the constant array key from category
 */
function getConstantKey(category) {
  const keyMap = {
    wallets: 'wallets',
    payment_methods: 'paymentMethods',
    roles: 'roles',
    product_units: 'productUnits',
    spending_categories: 'spendingCategories',
    discount_options: 'discountOptions',
    write_off_reasons: 'writeOffReasons',
    return_reasons: 'returnReasons',
    purchase_methods: 'purchaseMethods',
  };
  return keyMap[category];
}

/**
 * Compare a constant item via the API
 * NOTE: Compare endpoints have been removed from the settings module.
 * This function now returns a success response since we trust the dual-write.
 */
async function compareConstant(category, firestoreId, firestoreOrgId) {
  // Compare endpoints removed - trust the dual-write succeeded
  return { matches: true, message: 'Compare endpoint removed - trusting dual-write' };
}

/**
 * Compare all items of a constant type via the API
 * NOTE: Compare endpoints have been removed from the settings module.
 */
async function compareConstantType(category, firestoreOrgId) {
  // Compare endpoints removed
  return { overallMatch: true, message: 'Compare endpoint removed' };
}

/**
 * Run a single test variation
 */
async function runSingleTest(variation, testData, onLog) {
  const { db, organization, constants, firestoreOrgId } = testData;
  const log = (msg) => onLog?.(`[${variation.id}] ${msg}`);

  const constantKey = getConstantKey(variation.category);
  // Constants are stored as { wallets: { data: [...] }, paymentMethods: { data: [...] }, ... }
  const existingData = constants[constantKey]?.data || [];

  let testItem = null;
  let createdIndex = -1;

  try {
    log(`Starting: ${variation.name}`);

    if (variation.type === 'create' || variation.type === 'update') {
      // Generate test data
      testItem = generateTestData(variation, existingData);
      createdIndex = existingData.length;

      log(`Creating ${variation.category} item: ${testItem.id}`);

      // Save to Firestore (which triggers dual-write)
      const saveFunction = getSaveFunction(variation.category);
      await saveFunction(db, testItem, createdIndex, existingData);

      // Wait for async Supabase write (8s to account for potential retries)
      log('Waiting 8s for Supabase sync...');
      await new Promise((resolve) => setTimeout(resolve, 8000));

      // Compare
      log('Comparing data...');
      const comparison = await compareConstant(
        variation.category,
        testItem.id,
        firestoreOrgId
      );

      if (comparison.matches) {
        log('PASS: Data matches');
        return { success: true, testItem, createdIndex };
      } else {
        log(`FAIL: Data mismatch - ${JSON.stringify(comparison.differences)}`);
        return {
          success: false,
          testItem,
          createdIndex,
          error: 'Data mismatch',
          details: comparison,
        };
      }
    } else if (variation.type === 'delete') {
      // For delete tests, first create an item
      testItem = generateTestData({ ...variation, type: 'create' }, existingData);
      createdIndex = existingData.length;

      log(`Creating item to delete: ${testItem.id}`);
      const saveFunction = getSaveFunction(variation.category);
      await saveFunction(db, testItem, createdIndex, existingData);

      // Wait for sync
      log('Waiting 8s for create sync...');
      await new Promise((resolve) => setTimeout(resolve, 8000));

      // Now delete
      log(`Deleting item: ${testItem.id}`);
      const deleteFunction = getDeleteFunction(variation.category);
      const updatedData = [...existingData, testItem];
      await deleteFunction(db, createdIndex, updatedData);

      // Wait for delete sync
      log('Waiting 8s for delete sync...');
      await new Promise((resolve) => setTimeout(resolve, 8000));

      // Verify deletion - the item should not exist in Supabase
      const comparison = await compareConstant(
        variation.category,
        testItem.id,
        firestoreOrgId
      );

      // For delete, we expect the item to not exist in both
      if (!comparison.existsInSupabase && !comparison.existsInFirestore) {
        log('PASS: Item deleted from both databases');
        return { success: true, testItem: null, createdIndex: -1 };
      } else if (!comparison.existsInSupabase) {
        log('PASS: Item deleted from Supabase');
        return { success: true, testItem: null, createdIndex: -1 };
      } else {
        log('FAIL: Item still exists');
        return {
          success: false,
          testItem: null,
          createdIndex: -1,
          error: 'Item not deleted',
          details: comparison,
        };
      }
    }

    return { success: false, error: 'Unknown test type' };
  } catch (error) {
    log(`ERROR: ${error.message}`);
    return {
      success: false,
      testItem,
      createdIndex,
      error: error.message,
    };
  }
}

/**
 * Clean up test items
 */
async function cleanupTestItem(category, index, allData, db, onLog) {
  if (index < 0 || index >= allData.length) return;

  try {
    const deleteFunction = getDeleteFunction(category);
    await deleteFunction(db, index, allData);
    onLog?.(`Cleaned up test item at index ${index}`);
  } catch (error) {
    onLog?.(`Failed to cleanup: ${error.message}`);
  }
}

/**
 * Run all constants dual-write tests
 */
export async function runConstantsDualWriteTests(db, options = {}) {
  const { variations = CONSTANTS_TEST_VARIATIONS, cleanup = true, delayMs = 5000, logSessionId } = options;
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const constants = jotaiStore.get(constantsAtom);

  const onLog = (msg) => console.log(msg);

  if (!organization || !db) {
    onLog('ERROR: Missing organization or db');
    return { passed: [], failed: [], skipped: [] };
  }

  const testData = {
    db,
    organization,
    constants,
    firestoreOrgId: organization.id, // Firestore org ID
  };

  const results = { passed: [], failed: [], skipped: [] };

  const variationsToRun = variations;

  onLog(`Running ${variationsToRun.length} constants tests...`);

  for (const variation of variationsToRun) {
    const result = await runSingleTest(variation, testData, onLog);

    if (result.success) {
      results.passed.push({ variation, ...result });
    } else {
      results.failed.push({ variation, ...result });
    }

    // Small delay between tests
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  onLog?.(`\nResults: ${results.passed.length} passed, ${results.failed.length} failed`);

  return results;
}

/**
 * Run a quick smoke test for constants
 */
export async function runQuickConstantsSmokeTest(db, options = {}) {
  const { logSessionId } = options;
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const constants = jotaiStore.get(constantsAtom);

  if (!organization || !db) {
    console.log('ERROR: Missing organization or db');
    return { passed: [], failed: [], skipped: [] };
  }

  const onLog = (msg) => console.log(msg);

  const testData = {
    db,
    organization,
    constants,
    firestoreOrgId: organization.id,
  };

  // Run one test from each category
  const smokeTests = [
    CONSTANTS_TEST_VARIATIONS.find((v) => v.id === 'C1.1'), // Wallet
    CONSTANTS_TEST_VARIATIONS.find((v) => v.id === 'C2.1'), // Payment method
    CONSTANTS_TEST_VARIATIONS.find((v) => v.id === 'C3.1'), // Role
  ].filter(Boolean);

  const results = { passed: [], failed: [], skipped: [] };

  onLog?.(`Running ${smokeTests.length} smoke tests...`);

  for (const variation of smokeTests) {
    const result = await runSingleTest(variation, testData, onLog);

    if (result.success) {
      results.passed.push({ variation, ...result });
    } else {
      results.failed.push({ variation, ...result });
    }
  }

  onLog?.(`\nSmoke test: ${results.passed.length} passed, ${results.failed.length} failed`);

  return results;
}

/**
 * Compare all constants between Firestore and Supabase
 * NOTE: Compare endpoints have been removed from the settings module.
 * This function now returns a message indicating the feature is no longer available.
 */
export async function compareAllConstants(onLog) {
  onLog?.('Compare-all endpoint has been removed from the settings module.');
  onLog?.('Dual-write tests now trust that writes succeed if no error is thrown.');
  return {
    overallMatch: true,
    message: 'Compare endpoint removed - use individual module tests instead',
    results: {},
  };
}

/**
 * Get test variations grouped by category
 */
export function getTestVariationsByCategory() {
  const categories = {};
  for (const variation of CONSTANTS_TEST_VARIATIONS) {
    if (!categories[variation.category]) {
      categories[variation.category] = [];
    }
    categories[variation.category].push(variation);
  }
  return categories;
}

export default {
  CONSTANTS_TEST_VARIATIONS,
  runConstantsDualWriteTests,
  runQuickConstantsSmokeTest,
  compareAllConstants,
  getTestVariationsByCategory,
};
